import React, {useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Badge} from "primereact/badge";
import {Button} from "primereact/button";
import {OverlayPanel} from "primereact/overlaypanel";
import {timeSince} from "../utils";
import {useTranslate} from "../useTranslate";
import useUserEventLog from "../../models/user-event-log/use-user-event-log";
import {belongsToWorkspace, isIncomingItem} from "../../pages/inbox/inbox-utils";
import PushPromptBanner from "../push/push-prompt-banner";

// Toolbar bell: a quick preview of unread «اعلان‌ها» across workspaces (task 12, phase 5). Chat has its own counts in
// «گفتگوها»; the FYI activity feed is not part of the bell.

const key = (item) => item?.documentId ?? item?.id;
const PRIORITY_DOT = {urgent: "bg-red-500", high: "bg-orange-400"};

function targetOf(item) {
    const url = String(item?.actionUrl || "/inbox?tab=notifications").replace(/^#/, "");
    return `${url}${url.includes("?") ? "&" : "?"}notification=${encodeURIComponent(key(item))}`;
}

export default function NotificationBell({readOnly = false}) {
    const panel = useRef(null);
    const navigate = useNavigate();
    const translate = useTranslate();
    const {notifications, workspaceMembershipData} = useSelector((state) => state.app);
    const userEventLog = useUserEventLog();
    const [pendingKey, setPendingKey] = useState(null);
    // only the active workspace's notifications (plus account-level ones)
    const unread = (notifications || []).filter((item) => (
        !item.isRead && isIncomingItem(item) && belongsToWorkspace(item, workspaceMembershipData?.workspace)
    ));
    const count = unread.length;

    const markRead = async (item) => {
        if (readOnly || pendingKey) return;
        setPendingKey(key(item));
        try {
            await userEventLog.updateUserEventLog(key(item), {isRead: true});
        } catch (error) {
            console.error("[NotificationBell mark read]", error);
        } finally {
            setPendingKey(null);
        }
    };

    const open = (item) => {
        panel.current?.hide();
        navigate(targetOf(item));
    };

    const markAll = async () => {
        if (readOnly || pendingKey) return;
        setPendingKey("all");
        try {
            await userEventLog.markAllRead({allWorkspaces: true});
        } finally {
            setPendingKey(null);
        }
    };

    return (
        <>
            <button
                type="button"
                className="bg-transparent border-none p-0 pl-2 cursor-pointer p-overlay-badge"
                aria-label={count ? `${count} اعلان خوانده‌نشده` : "اعلان‌ها"}
                onClick={(event) => panel.current.toggle(event)}
            >
                <i className="text-2xl pi pi-bell text-color"/>
                {count > 0 && <Badge value={count > 99 ? "99+" : count} severity="danger" className="text-2xs"/>}
            </button>
            <OverlayPanel className="w-22rem rtl" ref={panel} pt={{content: () => ({className: "p-0"})}}>
                <div className="flex align-items-center justify-content-between px-3 py-2 border-bottom-1 border-100">
                    <strong className="text-sm">اعلان‌ها</strong>
                    {count > 0 && !readOnly && (
                        <Button label="همه خوانده شد" icon="pi pi-check-circle" text size="small" className="text-xs gap-1 p-1" loading={pendingKey === "all"} onClick={markAll}/>
                    )}
                </div>
                {!readOnly && <PushPromptBanner compact/>}
                <div className="overflow-auto" style={{maxHeight: "24rem"}}>
                    {count ? unread.slice(0, 30).map((item) => (
                        <div
                            key={key(item)}
                            role="button"
                            tabIndex={0}
                            className="flex align-items-start gap-2 px-3 py-2 border-bottom-1 border-50 cursor-pointer hover:surface-100"
                            onClick={() => open(item)}
                            onKeyDown={(event) => { if (event.key === "Enter") open(item); }}
                        >
                            <span className={`border-circle flex-shrink-0 mt-2 ${PRIORITY_DOT[item.priority] || "bg-primary"}`} style={{width: ".5rem", height: ".5rem"}}/>
                            <div className="flex flex-column gap-1 min-w-0 flex-1">
                                <span className="text-xs font-semibold text-900">{translate(item.title)}</span>
                                {item.message && <span className="text-xs text-600 white-space-nowrap overflow-hidden text-overflow-ellipsis">{translate(item.message)}</span>}
                                <span className="text-2xs text-400">
                                    {[item.contextLabel || item.workspace?.name, timeSince(new Date(item.createdAt) / 1000)].filter(Boolean).join(" · ")}
                                </span>
                            </div>
                            {!readOnly && (
                                <Button
                                    icon="pi pi-check"
                                    text
                                    rounded
                                    size="small"
                                    className="flex-shrink-0 w-2rem h-2rem"
                                    aria-label="خوانده شد"
                                    tooltip="خوانده شد"
                                    tooltipOptions={{position: "left"}}
                                    loading={pendingKey === key(item)}
                                    onClick={(event) => { event.stopPropagation(); markRead(item); }}
                                />
                            )}
                        </div>
                    )) : (
                        <div className="text-xs text-500 text-center p-4">اعلان خوانده‌نشده‌ای ندارید</div>
                    )}
                </div>
                <div className="flex align-items-center justify-content-between px-2 py-1 border-top-1 border-100">
                    <Button label="مشاهده همه" text size="small" className="text-xs" onClick={() => { panel.current.hide(); navigate("/inbox?tab=notifications"); }}/>
                    <Button label="تنظیمات" icon="pi pi-cog" text size="small" severity="secondary" className="text-xs gap-1" onClick={() => { panel.current.hide(); navigate("/notification-settings"); }}/>
                </div>
            </OverlayPanel>
        </>
    );
}
